import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useField } from 'formik';
import { StripeProvider, Elements } from 'react-stripe-elements';
import { toast } from 'react-toastify';
import Document, { createDocument } from '../../../models/be/db/Document';
import Blob from '../../../models/be/db/Blob';
import { hasValue, single, createQueryString, saveBase64File } from '../../../lib/utils';
import DropDownListField from '../../../commonComponents/fields/DropDownListField';
import './DocumentField.less';
import { isStripeCardDocumentType, getFileBlobName } from '../../../core/domain';
import StripeCreditCardForm from './StripeCreditCardForm';
import FileUploadField, { imageAndPdfFileExtensions } from '../../../commonComponents/fields/FileUploadField';
import { getFile, setDocument } from '../../../core/api';
import {
	PrimaryDocuments,
	CommencementDocuments,
	DbsSecondaryIdTypes,
	DbsIdTypes,
	DsIdTypes,
	DbsPrimaryIdentityDocuments,
	DbsFinancialAndSocialHistoryDocuments,
	DbsTrustedGovernmentDocuments,
	POADocuments,
	IDDocuments,
	NZPrimaryDocuments,
	NZSecondaryDocuments,
	SecondaryDocuments,
	GroupOneDocument,
	GroupTwoADocumentOne,
	GroupTwoADocumentTwo,
	GroupTwoBDocumentOne,
	GroupTwoBDocumentTwo
} from '../../../core/enums';
import DocumentFieldProps from '../../../models/dynamicForms/DocumentFieldProps';
import { addFieldNameForFileUpload, removeFieldNameForFileUpload } from '../../../core/actions';
import { useAsyncAction } from '../../../commonComponents/Hooks';
import Loading from '../../../commonComponents/Loading';
import MessageBox from '../MessageBox';

// This global dictionary is use to track all document types currently in use so that we can ensure that
// each document type is used only once.
const fieldNamesToDocumentTypes: Record<string, string> = {};

const DocumentField = (props: DocumentFieldProps) => {
	const dispatch = useDispatch();
	const fieldName = single(props.fieldNames);
	const formikProps = { name: fieldName };
	const [field, , helpers] = useField(formikProps);
	const { allowedExtensions = imageAndPdfFileExtensions } = props;
	const [loading, errorMessage, performAsyncAction] = useAsyncAction();

	const { workflowId, candidateId, customerId, refereeToken } = props;

	if (hasValue(candidateId) && hasValue(refereeToken)) {
		throw new Error('candidateId and refereeToken are mutually exclusive.');
	} else if (!hasValue(candidateId) && !hasValue(refereeToken)) {
		throw new Error('candidateId or refereeToken are required.');
	}

	const setDocumentAndDocumentField = async (document: Document) => {
		const cardDeclinedError = 'Your card was declined.';
		const sameBankAndCreditCardError =
			'Oops! Looks like you used the same card numbers for a bank card and credit card. ' +
			'Please use different cards if using both a bank and credit card, otherwise choose another document to upload.';
		const shouldHaveOnlyOneCardError = 'Should have only one card on creating new Stripe customer.';

		try {
			await setDocument(workflowId, candidateId, customerId, refereeToken, fieldName, document);
		} catch (e) {
			// There is a file cabinet error where e is coming back not as an object.
			// This does our intended error handling if e is a proper object, and throws the error if not.
			if (typeof e === 'object') {
				if (hasValue(e.body)) {
					if (e.body.startsWith(cardDeclinedError)) {
						toast(cardDeclinedError);
						return false;
					}
					if (e.body.startsWith(sameBankAndCreditCardError) || e.body.startsWith(shouldHaveOnlyOneCardError)) {
						toast(e.body);
						return false;
					}
				}
				toast(`Unknown error: ${JSON.stringify(e)}`);
				throw e;
			} else {
				throw e;
			}
		}
		helpers.setValue(document);
		return true;
	};

	const document = field.value as Document;

	// TODO: Find out why these states default to null? Is `?? null` necessary?
	const [description, setDescription] = React.useState<string>(document?.description ?? null);
	const [blob, setBlob] = React.useState<Blob>(document?.blob ?? null);
	const singleDocumentType =
		hasValue(props.documentTypes) && props.documentTypes.length === 1 ? props.documentTypes[0] : null;
	const [documentType, setDocumentType] = React.useState<string>(singleDocumentType);

	const { documentTypeSet } = props;

	React.useEffect(() => {
		if (hasValue(props.documentTypes) && hasValue(documentTypeSet)) {
			throw new Error('Both documentTypes and documentTypeSet can not be defined.');
		} else if (!hasValue(props.documentTypes) && !hasValue(documentTypeSet)) {
			throw new Error('documentTypes or documentTypeSet must be defined.');
		} else if (!hasValue(documentType) && (hasValue(documentTypeSet) || hasValue(documentTypes))) {
			setDocumentType(document?.documentType);
			// TODO: Why does it check `undefined`? Why not check `null`?
			// Update - 14/12/2020: An optional prop somewhere can be undefined, and so it is set to undefined. Or at least
			// it was that was until Andrew set defaultProps for all optional props, which may mean it now defaults to null.
		} else if (document?.documentType !== undefined && document?.documentType !== singleDocumentType) {
			// If there is only a documentType and no documentTypeSet
			setDocumentAndDocumentField(createDocument(documentType, description));
		}

		return () => {
			delete fieldNamesToDocumentTypes[fieldName];
		};
	}, []);

	// Since it possible that the description and the blob change within the same React lifecycle (if that is the correct
	// term) as the user can upload a file and then start editting the description and then the file has finished
	// uploading and the blob is set we have to track their values with hooks and use an effect hook to send the ajax
	// request to the BE.
	const firstUpdate = React.useRef(true);
	React.useEffect(() => {
		// useEffect is run in first update of component, we want to update document only when user updates desciption or blob.
		if (firstUpdate.current) firstUpdate.current = false;
		else {
			updateDocument();
		}
	}, [documentType, description, blob]);

	React.useEffect(() => {
		if (documentType) {
			fieldNamesToDocumentTypes[fieldName] = documentType;
		}
	}, [documentType]);

	const documentTypes = getDocumentTypes(
		props.documentTypes,
		documentTypeSet,
		props.includedDocumentTypes,
		props.excludedDocumentTypes
	);
	if (documentTypes !== null && hasValue(documentType) && documentTypes.indexOf(documentType) === -1) {
		// 1. The documentType was previously a valid value but now something has changed like the same field
		// being used in a different section with different props and the documentType needs to be reset.
		// 2. Set document to null, the field props will be updated, but initial states won't be reset, so must reset here.
		setDocumentType(null);
		setDescription(null);
		setBlob(null);
		setDocumentAndDocumentField(createDocument(null));
	}

	const updateDocument = async () => {
		const newDocument: Document = {
			...createDocument(documentType, description),
			blob,
			stripeCardDetails: document?.stripeCardDetails
		};
		await setDocumentAndDocumentField(newDocument);
	};

	const handleFileUpload = async (isUploading: boolean, newBlob: Blob): Promise<void> => {
		if (isUploading) {
			dispatch(addFieldNameForFileUpload(fieldName));
			props.onFileUpload?.();
		} else {
			dispatch(removeFieldNameForFileUpload(fieldName));
		}

		if (hasValue(newBlob)) {
			setBlob(newBlob);
		}
	};

	const handleDocumentTypeValueChange = async (_name: string, value: string) => {
		if (hasValue(value) && documentType !== value) {
			const documentTypeAlreadySelected = Object.values(fieldNamesToDocumentTypes).indexOf(value) !== -1;
			if (documentTypeAlreadySelected) {
				toast(`'${value}' document type has already been selected.`);
				return;
			}

			setDocumentType(value);
			if (isStripeCardDocumentType(value)) {
				setBlob(null);
				setDescription(null);
			}
		}
	};

	const handleDeleteFile = async (): Promise<void> => {
		await setDocument(workflowId, candidateId, customerId, refereeToken, fieldName, null);
		setBlob(null);
	};

	const onDeleteButtonClick = async (updatedDocumentType: string): Promise<void> => {
		const newDocument: Document = createDocument(updatedDocumentType, description);
		await setDocumentAndDocumentField(newDocument);
	};

	const handleVerifyCard = async (updatedDocument: Document): Promise<boolean> => {
		return await setDocumentAndDocumentField(updatedDocument);
	};

	// There is a async issue when we use onBlur. The description might not be updated before form submit.
	const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newDescription = e.target.value;
		setDescription(newDescription || null);
	};

	const handleDownload = async () => {
		await performAsyncAction(async () => {
			const fileString = await getFile(blob.fileId);
			saveBase64File(fileString, blob.originalFileName);
		});
	};

	const { title } = props;

	return (
		<>
			{title && <label className="label">{title}</label>}
			{documentTypes && (
				<DropDownListField
					name="documentType"
					data={documentTypes}
					value={documentType}
					onValueChange={handleDocumentTypeValueChange}
					required
					className="documentFieldDropdownlist"
				/>
			)}
			{props.fileDescriptionLabel && (
				<>
					<label className="label" htmlFor="description">
						{props.fileDescriptionLabel}
					</label>
					<input
						id="description"
						className="k-textbox"
						name="description"
						value={description ?? ''}
						onChange={handleDescriptionChange}
						required
					/>
				</>
			)}
			{isStripeCardDocumentType(documentType) ? (
				props.stripe && (
					<StripeProvider stripe={props.stripe}>
						<Elements>
							<StripeCreditCardForm
								cardType={documentType}
								stripeCardDetails={document?.stripeCardDetails}
								onVerifyCard={handleVerifyCard}
								onDeleteButtonClick={onDeleteButtonClick}
							/>
						</Elements>
					</StripeProvider>
				)
			) : (
				<>
					<div className="fileUploadField">
						<FileUploadField
							key={documentType}
							fileName={getFileBlobName(document?.blob)}
							fileId={document?.blob?.fileId}
							uploadUrl="api/Files/RequestId"
							queryString={getQueryStringForDocumentUpload(candidateId, customerId, workflowId, refereeToken)}
							onFileUpload={handleFileUpload}
							allowedExtensions={allowedExtensions}
							disabled={!hasValue(documentType)}
							onDeleteFile={handleDeleteFile}
						/>
					</div>
					{props.documentIsDownloadable && hasValue(document) && !loading && <a onClick={handleDownload}>Download</a>}
					{loading && <Loading />}
					<MessageBox error={errorMessage} />
				</>
			)}
		</>
	);
};

DocumentField.defaultProps = {
	uploadedDocumentIsDownloadable: false
};

export default DocumentField;

export const getQueryStringForDocumentUpload = (
	candidateId: string,
	customerId: number,
	workflowId: string = null,
	refereeToken: string = null
) => {
	return createQueryString({ workflowId, candidateId, customerId, refereeToken });
};

const getDocumentTypes = (
	documentTypes?: string[],
	documentTypeSet?: string,
	includedDocumentTypes?: string[],
	excludedDocumentTypes?: string[]
): string[] => {
	if (hasValue(documentTypes) && documentTypes.length > 1) {
		return documentTypes;
	}

	if (!hasValue(documentTypeSet)) {
		return null;
	}

	const documentTypesForSet = getDocumentTypesForSet(documentTypeSet).concat(includedDocumentTypes ?? []);

	return hasValue(excludedDocumentTypes)
		? documentTypesForSet.filter((t) => excludedDocumentTypes.indexOf(t) === -1)
		: documentTypesForSet;
};

const getDocumentTypesForSet = (documentTypeSet: string): string[] => {
	if (documentTypeSet === 'primaryDocuments') {
		return Object.values(PrimaryDocuments);
	}
	if (documentTypeSet === 'nzPrimaryDocuments') {
		return Object.values(NZPrimaryDocuments);
	}
	if (documentTypeSet === 'nzSecondaryDocuments') {
		return Object.values(NZSecondaryDocuments);
	}
	if (documentTypeSet === 'poaDocuments') {
		return Object.values(POADocuments);
	}
	if (documentTypeSet === 'idDocuments') {
		return Object.values(IDDocuments);
	}
	if (documentTypeSet === 'secondaryDocuments') {
		return Object.values(SecondaryDocuments);
	}
	if (documentTypeSet === 'groupOneDocument') {
		return Object.values(GroupOneDocument);
	}
	if (documentTypeSet === 'groupTwoADocumentOne') {
		return Object.values(GroupTwoADocumentOne);
	}
	if (documentTypeSet === 'groupTwoADocumentTwo') {
		return Object.values(GroupTwoADocumentTwo);
	}
	if (documentTypeSet === 'groupTwoBDocumentOne') {
		return Object.values(GroupTwoBDocumentOne);
	}
	if (documentTypeSet === 'groupTwoBDocumentTwo') {
		return Object.values(GroupTwoBDocumentTwo);
	}
	if (documentTypeSet === 'commencementDocuments') {
		return Object.values(CommencementDocuments);
	}
	if (documentTypeSet === 'dbsIdTypes') {
		return Object.values(DbsIdTypes);
	}
	if (documentTypeSet === 'dbsSecondaryIdTypes') {
		return Object.values(DbsSecondaryIdTypes);
	}
	if (documentTypeSet === 'dsIdTypes') {
		return Object.values(DsIdTypes);
	}
	if (documentTypeSet === 'dbsPrimaryIdentityDocuments') {
		return Object.values(DbsPrimaryIdentityDocuments);
	}
	if (documentTypeSet === 'dbsFinancialAndSocialHistoryDocuments') {
		return Object.values(DbsFinancialAndSocialHistoryDocuments);
	}
	if (documentTypeSet === 'allDbsDocuments') {
		const documents = (Object.values(DbsPrimaryIdentityDocuments) as string[])
			.concat(Object.values(DbsTrustedGovernmentDocuments))
			.concat(Object.values(DbsFinancialAndSocialHistoryDocuments));
		return Array.from(new Set(documents));
	}
	throw new Error(`Unknown documentStypeSet: ${documentTypeSet}.`);
};
